import React, { useState, useEffect, useRef, useCallback } from 'react';
import PdfControls from './PdfControls';
import { useImageLoader } from '../hooks/useImageLoader';
import { SERVER_URL } from '../../../DB_Url';
import { AuthUserContext } from '../../Session';
import { withFirebase } from '../../Firebase';
import { useProjectSelection } from '../../ProjectSelection';
import * as pdfjsLib from 'pdfjs-dist';
import { Stage, Layer, Image as KonvaImage } from 'react-konva';
import { useStageInteraction } from '../hooks/useStageInteraction';

// Configure PDF.js worker path
pdfjsLib.GlobalWorkerOptions.workerSrc = `/pdf.worker.mjs`;

const PdfStreamTestBase = ({ firebase }) => {
  const { selectedProject: projectName, setSelectedProject: setProjectName } = useProjectSelection();
  const [versionName, setVersionName] = useState('');
  const [folderPath, setFolderPath] = useState('');

  const {
    pages,
    numImages,
    currentImageIndex,
    currentPageData,
    goToPage
  } = useImageLoader(projectName, versionName, folderPath, { disableImageFetch: true });

  const [pdfUrl, setPdfUrl] = useState(null);
  const [pdfDoc, setPdfDoc] = useState(null);
  const [loadingPdf, setLoadingPdf] = useState(false);
  const [error, setError] = useState(null);

  const containerRef = useRef(null);
  const stageRef = useRef(null);

  // Base Low-Res Image
  const [basePdfImage, setBasePdfImage] = useState(null);
  const [pdfDimensions, setPdfDimensions] = useState({ width: 800, height: 600 });
  const [containerDimensions, setContainerDimensions] = useState({ width: 800, height: 600 });

  // High-Res Overlay State
  const [highResImage, setHighResImage] = useState(null);
  const [highResBounds, setHighResBounds] = useState(null);
  const highResRenderTask = useRef(null);
  const renderTimeout = useRef(null);

  // Handle Container Resizing
  useEffect(() => {
    if (!containerRef.current) return;
    const updateDims = () => {
      if (containerRef.current) {
        setContainerDimensions({
          width: containerRef.current.clientWidth,
          height: containerRef.current.clientHeight
        });
      }
    };
    updateDims();
    window.addEventListener('resize', updateDims);
    setTimeout(updateDims, 100);
    return () => window.removeEventListener('resize', updateDims);
  }, [pdfUrl, loadingPdf]);

  const {
    stageScale,
    stagePos,
    handleWheel,
    handleStageDragEnd,
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
    resetView
  } = useStageInteraction(stageRef, pdfDimensions, containerDimensions);

  // When zooming/panning, clear the high-res overlay to prevent visual tearing
  // We check stringified values to trigger only on significant structural changes
  useEffect(() => {
    setHighResImage(null);
  }, [stageScale, stagePos.x, stagePos.y]);

  // Fetch the PDF URL
  useEffect(() => {
    if (!currentPageData || !currentPageData.documentId) {
      setPdfUrl(null);
      return;
    }
    const fetchPdfUrl = async () => {
      setLoadingPdf(true);
      setError(null);
      try {
        const response = await fetch(`${SERVER_URL}/api/document/${currentPageData.documentId}/pdf-url`);
        if (!response.ok) throw new Error(`Failed to fetch PDF URL endpoint: ${response.status}`);
        const data = await response.json();
        if (data.url) setPdfUrl(data.url);
        else if (data.error) setError(data.error);
        else setError('Unexpected response format');
      } catch (err) {
        setError(`Failed to retrieve stream URL: ${err.message}`);
      } finally {
        setLoadingPdf(false);
      }
    };
    fetchPdfUrl();
  }, [currentPageData?.documentId]);

  // Load the PDF via pdfjs
  useEffect(() => {
    if (!pdfUrl) {
      setPdfDoc(null);
      return;
    }
    let isSubscribed = true;
    const loadPdfDoc = async () => {
      setLoadingPdf(true);
      try {
        const loadingTask = pdfjsLib.getDocument({
          url: pdfUrl,
          disableAutoFetch: true,
          disableStream: true,
          disableRange: false
        });
        const pdf = await loadingTask.promise;
        if (isSubscribed) setPdfDoc(pdf);
      } catch (err) {
        if (isSubscribed) setError(`Failed to load PDF document: ${err.message}`);
      } finally {
        if (isSubscribed) setLoadingPdf(false);
      }
    };
    loadPdfDoc();
    return () => { isSubscribed = false; };
  }, [pdfUrl]);

  // 1. Initial BASE rendering (Lightning Fast 1.0x Scale)
  useEffect(() => {
    if (!pdfDoc || !currentPageData) return;
    let renderTask = null;
    let isSubscribed = true;

    const renderBasePage = async () => {
      setLoadingPdf(true);
      setError(null);
      setBasePdfImage(null);
      setHighResImage(null);

      try {
        const pageNum = parseInt(currentPageData.actualPageNumber, 10);
        if (isNaN(pageNum) || pageNum < 1 || pageNum > pdfDoc.numPages) {
          throw new Error(`Requested page ${pageNum} is out of bounds`);
        }

        const page = await pdfDoc.getPage(pageNum);
        if (!isSubscribed) return;

        // Base render is executed at EXACTLY 1.0x (ultra-fast compute)
        // Since we have the dynamic surgical high-res zoom, there is no need to make the base layer expensive!
        const unscaledViewport = page.getViewport({ scale: 1.0 });
        const viewport = page.getViewport({ scale: 1.0 });
        const canvas = document.createElement('canvas');
        canvas.height = viewport.height;
        canvas.width = viewport.width;

        const renderContext = {
          canvasContext: canvas.getContext('2d'),
          viewport,
          background: 'rgba(255,255,255,0)' // Force PDF background to be perfectly transparent
        };
        renderTask = page.render(renderContext);
        await renderTask.promise;

        if (isSubscribed) {
          // We explicitly store the UNSCALED document bounds so Konva geometry maps 1:1 natively
          setPdfDimensions({ width: unscaledViewport.width, height: unscaledViewport.height });
          setBasePdfImage(canvas);
        }
      } catch (err) {
        if (isSubscribed) setError(`Failed to render page content: ${err.message}`);
      } finally {
        if (isSubscribed) setLoadingPdf(false);
      }
    };

    renderBasePage();
    return () => {
      isSubscribed = false;
      if (renderTask) renderTask.cancel();
    };
  }, [pdfDoc, currentPageData]);

  // 2. High-Res Sector Rendering (Debounced)
  useEffect(() => {
    if (!pdfDoc || !currentPageData || !basePdfImage) return;

    // Don't bother doing high-res math if we are completely zoomed out
    if (stageScale <= 1.0) {
      return;
    }

    // Clear previous pending job
    if (renderTimeout.current) clearTimeout(renderTimeout.current);

    // Debounce the high-res rendering so we don't spam the CPU while actively dragging/zooming
    renderTimeout.current = setTimeout(async () => {
      try {
        if (highResRenderTask.current) {
          highResRenderTask.current.cancel();
        }

        const pageNum = parseInt(currentPageData.actualPageNumber, 10);
        const page = await pdfDoc.getPage(pageNum);

        // Calculate the absolutely visible dimensions in the Konva Stage
        // Unscaled coordinates of what bounds fall within the window container
        const visibleX = Math.max(0, -stagePos.x / stageScale);
        const visibleY = Math.max(0, -stagePos.y / stageScale);

        const visibleWidth = Math.min(
          pdfDimensions.width - visibleX,
          containerDimensions.width / stageScale
        );
        const visibleHeight = Math.min(
          pdfDimensions.height - visibleY,
          containerDimensions.height / stageScale
        );

        if (visibleWidth <= 0 || visibleHeight <= 0) return;

        // Apply dynamic scale depending on zoom, capped to prevent VRAM memory crash
        const dpr = window.devicePixelRatio || 1;
        const targetOutputScale = Math.min(Math.max(stageScale * dpr, 2.0), 6.0); // Never process past 6.0x res

        const canvas = document.createElement('canvas');
        canvas.width = Math.ceil(visibleWidth * targetOutputScale);
        canvas.height = Math.ceil(visibleHeight * targetOutputScale);
        const context = canvas.getContext('2d');

        // Configure viewport to precisely frame the targeted rectangle
        const viewport = page.getViewport({
          scale: targetOutputScale,
          offsetX: -visibleX * targetOutputScale,
          offsetY: -visibleY * targetOutputScale
        });

        const renderContext = {
          canvasContext: context,
          viewport,
          background: 'rgba(255,255,255,0)'
        };
        highResRenderTask.current = page.render(renderContext);

        await highResRenderTask.current.promise;

        setHighResBounds({
          x: visibleX,
          y: visibleY,
          width: visibleWidth,
          height: visibleHeight
        });
        setHighResImage(canvas);

      } catch (err) {
        if (err.name !== 'RenderingCancelledException') {
          console.error("High-res render failed", err);
        }
      }
    }, 400); // 400ms debounce before fetching high-res patch

    return () => {
      if (renderTimeout.current) clearTimeout(renderTimeout.current);
      if (highResRenderTask.current) highResRenderTask.current.cancel();
    };
  }, [stageScale, stagePos.x, stagePos.y, pdfDoc, currentPageData, basePdfImage, pdfDimensions, containerDimensions]);


  const handlePageChange = (newPage) => {
    goToPage(newPage - 1);
  };

  return (
    <AuthUserContext.Consumer>
      {authUser => (
        <div style={{ display: 'flex', flexDirection: 'column', height: '100vh', width: '100%', overflow: 'hidden' }}>
          <PdfControls
            pdfDoc={null}
            stageScale={stageScale}
            currentPage={currentImageIndex + 1}
            numPages={numImages}
            pages={pages}
            onPageChange={handlePageChange}
            projectName={projectName}
            versionName={versionName}
            onProjectChange={setProjectName}
            onVersionChange={setVersionName}
            selectedFolder={folderPath}
            onFolderChange={setFolderPath}
            measurementSystem="metric"
            onMeasurementSystemChange={() => { }}
            selectedScaleKey="custom"
            onSelectedScaleKeyChange={() => { }}
            authUser={authUser}
            firebase={firebase}
            customScales={[]}
            setCustomScales={() => { }}
          />

          <div ref={containerRef} style={{ flex: 1, backgroundColor: '#cfd8dc', position: 'relative' }}>
            {error && (
              <div style={{ position: 'absolute', top: 50, zIndex: 10, left: '50%', transform: 'translateX(-50%)', padding: '10px', backgroundColor: '#ffebee', color: '#c62828', borderRadius: '4px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)' }}>
                {error}
              </div>
            )}

            {loadingPdf && (
              <div style={{ position: 'absolute', top: 50, zIndex: 10, left: '50%', transform: 'translateX(-50%)', padding: '10px 20px', backgroundColor: 'rgba(255,255,255,0.9)', color: '#455a64', borderRadius: '20px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)', fontWeight: 'bold' }}>
                Streaming Rendering...
              </div>
            )}

            {pdfUrl && (
              <div style={{ position: 'absolute', top: 50, left: 10, zIndex: 1000, padding: '5px', backgroundColor: '#e3f2fd', color: '#0d47a1', borderRadius: '4px', fontSize: '10px', maxWidth: '300px', overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>
                <a href={pdfUrl} target="_blank" rel="noopener noreferrer">{pdfUrl}</a>
              </div>
            )}

            {basePdfImage && !loadingPdf && (
              <Stage
                ref={stageRef}
                width={containerDimensions.width}
                height={containerDimensions.height}
                onWheel={handleWheel}
                draggable
                onDragEnd={handleStageDragEnd}
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
                scaleX={stageScale}
                scaleY={stageScale}
                x={stagePos.x}
                y={stagePos.y}
              >
                <Layer>
                  {/* The Full Page Base Layer (Fast, Low Memory) */}
                  <KonvaImage
                    image={basePdfImage}
                    width={pdfDimensions.width}
                    height={pdfDimensions.height}
                  />

                  {/* The Surgical High-Res Zoom Overlay */}
                  {highResImage && highResBounds && (
                    <KonvaImage
                      image={highResImage}
                      x={highResBounds.x}
                      y={highResBounds.y}
                      width={highResBounds.width}
                      height={highResBounds.height}
                      listening={false} // Prevent hover events blocking the base image
                    />
                  )}
                </Layer>
              </Stage>
            )}

            {(!pdfDoc || !currentPageData) && !loadingPdf && !error && (
              <div style={{ color: '#546e7a', position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', textAlign: 'center', fontSize: '18px' }}>
                Please select a project and page from the controls above to test the PDF stream.
              </div>
            )}

          </div>
        </div>
      )}
    </AuthUserContext.Consumer>
  );
};

export const PdfStreamTest = withFirebase(PdfStreamTestBase);
export default PdfStreamTest;
